import React, { useState, useEffect } from 'react';
import { 
  Layout, Card, Table, Tabs, Statistic, Row, Col, Button, Modal, 
  Typography, Space, Alert, Progress, Tag, Divider, List, Timeline,
  Form, InputNumber
} from 'antd';
import { 
  DollarOutlined, BarChartOutlined, TrophyOutlined, 
  CalculatorOutlined, DownloadOutlined, EyeOutlined
} from '@ant-design/icons';
import DatabaseManager from '../utils/DatabaseManager';
import CommissionCalculator from '../utils/CommissionCalculator';
import * as XLSX from 'xlsx';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import InteractiveSimulator from './InteractiveSimulator';


const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const EmployeeDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [comisionesMensuales, setComisionesMensuales] = useState([]);
  const [comisionesTrimestrales, setComisionesTrimestrales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [proyecciones, setProyecciones] = useState([]);
  const [resumen, setResumen] = useState({
    totalAnual: 0,
    promedioMensual: 0,
    mejorMes: 0,
    mejorTrimestre: 0,
    cumplimientoPromedio: 0
  });

  const [medios, setMedios] = useState([]);



  useEffect(() => {
    loadEmployeeData();
  }, [user]);

  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      
      // Load monthly commissions
      const mensuales = await DatabaseManager.CalculoComision.findAll({
        where: { 
          ejecutivoId: user.id,
          anio: currentYear,
          tipo: 'mensual',
          activo: true
        },
        include: [
          { model: DatabaseManager.Ciudad },
          { model: DatabaseManager.Medio }
        ],
        order: [['mes', 'ASC']]
      });
      setComisionesMensuales(mensuales);

      // Load quarterly commissions
      const trimestrales = await DatabaseManager.CalculoComision.findAll({
        where: { 
          ejecutivoId: user.id,
          anio: currentYear,
          tipo: 'trimestral',
          activo: true
        },
        include: [
          { model: DatabaseManager.Ciudad },
          { model: DatabaseManager.Medio }
        ],
        order: [['trimestre', 'ASC']]
      });
      setComisionesTrimestrales(trimestrales);

      // Load all available media
      const allMedios = await DatabaseManager.Medio.findAll();
      setMedios(allMedios);

      // Load projections for the first available medium
      const calculator = new CommissionCalculator();
      const firstMedioId = allMedios.length > 0 ? allMedios[0].id : 1;
      
      const proyeccionesResult = await calculator.calcularProyecciones(
        user.id,
        user.ciudadId,
        firstMedioId,
        currentYear,
        new Date().getMonth() + 1
      );
      
      if (proyeccionesResult.success) {
        setProyecciones(proyeccionesResult.data);
      }
      
      // Calculate summary
      calculateSummary(mensuales, trimestrales);

    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (mensuales, trimestrales) => {
    const comisionesMensuales = mensuales.map(m => m.comision);
    const comisionesTrimestrales = trimestrales.map(t => t.comision);
    
    const totalAnual = [...comisionesMensuales, ...comisionesTrimestrales].reduce((sum, c) => sum + c, 0);
    const promedioMensual = comisionesMensuales.length > 0 ? 
      comisionesMensuales.reduce((sum, c) => sum + c, 0) / comisionesMensuales.length : 0;
    const mejorMes = comisionesMensuales.length > 0 ? Math.max(...comisionesMensuales) : 0;
    const mejorTrimestre = comisionesTrimestrales.length > 0 ? Math.max(...comisionesTrimestrales) : 0;
    
    const cumplimientoPromedio = mensuales.length > 0 ?
      mensuales.reduce((sum, m) => sum + m.cumplimiento, 0) / mensuales.length : 0;

    setResumen({
      totalAnual,
      promedioMensual,
      mejorMes,
      mejorTrimestre,
      cumplimientoPromedio
    });
  };

  const handleViewDetails = (calculation) => {
    setSelectedCalculation(calculation);
    setModalVisible(true);
  };

  const handleExportPersonal = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Personal summary sheet
      const summarySheet = XLSX.utils.json_to_sheet([{
        'Nombre': user.nombre,
        'Ciudad': user.ciudad,
        'Total Anual': resumen.totalAnual,
        'Promedio Mensual': resumen.promedioMensual,
        'Mejor Mes': resumen.mejorMes,
        'Mejor Trimestre': resumen.mejorTrimestre,
        'Cumplimiento Promedio': (resumen.cumplimientoPromedio * 100).toFixed(2) + '%'
      }]);

      // Monthly commissions sheet
      const monthlySheet = XLSX.utils.json_to_sheet(
        comisionesMensuales.map(m => ({
          Mes: m.mes,
          Medio: m.Medio.nombre,
          Presupuesto: m.presupuesto,
          "Venta Real": m.ventaReal,
          Cumplimiento: (m.cumplimiento * 100).toFixed(2) + '%',
          Comisión: m.comision,
          'Puede Pagar': m.puedePagar ? 'Sí' : 'No'
        }))
      );

      // Quarterly commissions sheet
      const quarterlySheet = XLSX.utils.json_to_sheet(
        comisionesTrimestrales.map(t => ({
          Trimestre: t.trimestre,
          Medio: t.Medio.nombre,
          Presupuesto: t.presupuesto,
          "Venta Real": t.ventaReal,
          Cumplimiento: (t.cumplimiento * 100).toFixed(2) + '%',
          Comisión: t.comision,
          'Puede Pagar': t.puedePagar ? 'Sí' : 'No'
        }))
      );

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumen Personal');
      XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Comisiones Mensuales');
      XLSX.utils.book_append_sheet(workbook, quarterlySheet, 'Comisiones Trimestrales');

      XLSX.writeFile(workbook, `comisiones_${user.nombre.replace(/\s+/g, '_')}_${new Date().getFullYear()}.xlsx`);
      message.success('Exportación completada');
    } catch (error) {
      message.error('Error al exportar: ' + error.message);
    }
  };

  const calcularSimulacion = (values) => {
    // Valores base para el ejemplo como para niños
    const tasaBase = 0.005; // 0.5% base
    const multiplicadorMensual = 0.3; // 30% de la bolsa
    const multiplicadorTrimestral = 0.7; // 70% de la bolsa
    
    let totalVentas = 0;
    let totalMensual = 0;
    let totalTrimestral = 0;
    const resultadosPorMedio = [];

    medios.forEach(medioObj => {
      const medio = medioObj.nombre;
      const ppto = values[`ppto_${medioObj.id}`] || 0;
      const venta = values[`venta_${medioObj.id}`] || 0;
      
      let cumplimiento = ppto > 0 ? venta / ppto : 0;
      
      // Lógica simple de bandas (Ejemplo para niños)
      let factor = 0;
      let mensajeBanda = "";
      if (cumplimiento >= 1.0) {
        factor = 1.2; // 120%
        mensajeBanda = "¡Súper Estrella! 🌟 Pasaste la meta (Factor x1.2)";
      } else if (cumplimiento >= 0.85) {
        factor = 1.0; // 100%
        mensajeBanda = "¡Buen Trabajo! 👍 Cumpliste lo esperado (Factor x1.0)";
      } else if (cumplimiento >= 0.7) {
        factor = 0.5; // 50%
        mensajeBanda = "¡Casi! 😅 Te faltó un poco (Factor x0.5)";
      } else {
        factor = 0; // 0%
        mensajeBanda = "Ups 📉 Hay que esforzarse más (Factor x0)";
      }

      const comisionMensualBase = venta * tasaBase * multiplicadorMensual * factor;
      const comisionTrimestralBase = venta * tasaBase * multiplicadorTrimestral * factor;

      totalVentas += venta;
      totalMensual += comisionMensualBase;
      totalTrimestral += comisionTrimestralBase;

      resultadosPorMedio.push({
        medio,
        ppto,
        venta,
        cumplimiento: cumplimiento * 100,
        factor,
        mensajeBanda,
        comisionMensual: comisionMensualBase,
        comisionTrimestral: comisionTrimestralBase,
        comisionTotal: comisionMensualBase + comisionTrimestralBase
      });
    });

    const salarioAnual = (user.salarioBase || 2000000) * 12;
    const comisionAnualizada = (totalMensual + totalTrimestral) * 4; // Suponiendo que esto es un trimestre y multiplicamos x4

    setSimulacionResultados({
      totalVentas,
      comisionMensual: totalMensual,
      comisionTrimestral: totalTrimestral,
      comisionTotal: totalMensual + totalTrimestral,
      salarioAnual,
      comisionAnualizada,
      porcentajeDelSalario: salarioAnual > 0 ? (comisionAnualizada / salarioAnual) * 100 : 0,
      detalles: resultadosPorMedio
    });
  };


  const monthlyColumns = [
    {
      title: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      render: (mes) => `Mes ${mes}`,
    },
    {
      title: 'Medio',
      dataIndex: 'Medio.nombre',
      key: 'medio',
    },
    {
      title: 'Presupuesto',
      dataIndex: 'presupuesto',
      key: 'presupuesto',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Venta Real',
      dataIndex: 'ventaReal',
      key: 'ventaReal',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Cumplimiento',
      dataIndex: 'cumplimiento',
      key: 'cumplimiento',
      render: (value) => (
        <Progress 
          percent={(value * 100).toFixed(1)} 
          size="small"
          strokeColor={value >= 1 ? '#52c41a' : value >= 0.8 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Comisión',
      dataIndex: 'comision',
      key: 'comision',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Estado',
      dataIndex: 'puedePagar',
      key: 'puedePagar',
      render: (puedePagar) => (
        <Tag color={puedePagar ? 'green' : 'red'}>
          {puedePagar ? 'Pagar' : 'Bloqueado'}
        </Tag>
      ),
    },
    {
      title: 'Detalles',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const quarterlyColumns = [
    {
      title: 'Trimestre',
      dataIndex: 'trimestre',
      key: 'trimestre',
      render: (trimestre) => `T${trimestre}`,
    },
    {
      title: 'Medio',
      dataIndex: 'Medio.nombre',
      key: 'medio',
    },
    {
      title: 'Presupuesto',
      dataIndex: 'presupuesto',
      key: 'presupuesto',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Venta Real',
      dataIndex: 'ventaReal',
      key: 'ventaReal',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Cumplimiento',
      dataIndex: 'cumplimiento',
      key: 'cumplimiento',
      render: (value) => (
        <Progress 
          percent={(value * 100).toFixed(1)} 
          size="small"
          strokeColor={value >= 1 ? '#52c41a' : value >= 0.8 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Comisión',
      dataIndex: 'comision',
      key: 'comision',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Estado',
      dataIndex: 'puedePagar',
      key: 'puedePagar',
      render: (puedePagar) => (
        <Tag color={puedePagar ? 'green' : 'red'}>
          {puedePagar ? 'Pagar' : 'Bloqueado'}
        </Tag>
      ),
    },
    {
      title: 'Detalles',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const proyeccionColumns = [
    {
      title: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
    },
    {
      title: 'Presupuesto',
      dataIndex: 'presupuesto',
      key: 'presupuesto',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Proyección',
      dataIndex: 'proyeccion',
      key: 'proyeccion',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
    {
      title: 'Cumplimiento Estimado',
      dataIndex: 'cumplimientoEstimado',
      key: 'cumplimientoEstimado',
      render: (value) => (
        <Progress 
          percent={(value * 100).toFixed(1)} 
          size="small"
          strokeColor={value >= 1 ? '#52c41a' : value >= 0.8 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Comisión Estimada',
      dataIndex: 'comisionEstimada',
      key: 'comisionEstimada',
      render: (value) => `$${value.toLocaleString('es-CO')}`,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#001529', padding: '0 20px' }}>
        <div style={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '20px' }}>
            Panel de Ejecutivo - Simulador de Comisiones RCN
          </h1>
          <div>
            <span style={{ marginRight: '20px' }}>
              {user.nombre}
            </span>
            <span style={{ color: '#52c41a' }}>
              {user.ciudad} | {user.perfilVenta || 'Vendedor'}
            </span>
          </div>
        </div>
      </Header>

      <Content style={{ padding: '20px', background: '#f0f2f5' }}>

        <Card>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            tabBarExtraContent={
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={handleExportPersonal}
              >
                Exportar Mis Datos
              </Button>
            }
          >
            <TabPane tab="Resumen General" key="overview">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Card title="Rendimiento Mensual" style={{ height: '400px' }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Progress 
                        type="circle" 
                        percent={(resumen.cumplimientoPromedio * 100).toFixed(1)}
                        size={150}
                        strokeColor={resumen.cumplimientoPromedio >= 1 ? '#52c41a' : 
                                   resumen.cumplimientoPromedio >= 0.8 ? '#faad14' : '#ff4d4f'}
                        format={(percent) => `${percent}%`}
                      />
                      <div style={{ marginTop: '16px' }}>
                        <Title level={4} style={{ color: resumen.cumplimientoPromedio >= 1 ? '#52c41a' : '#ff4d4f' }}>
                          {resumen.cumplimientoPromedio >= 1 ? 'Excelente' : 
                           resumen.cumplimientoPromedio >= 0.8 ? 'Bueno' : 'Mejorable'}
                        </Title>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Proyecciones Próximos Meses">
                    <Table 
                      columns={proyeccionColumns}
                      dataSource={proyecciones}
                      rowKey="mes"
                      pagination={false}
                      size="small"
                    />
                  </Card>

                  <Card title="Evolución de Comisiones Mensuales" style={{ marginTop: '16px' }}>
                    <div style={{ height: 300, width: '100%' }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={comisionesMensuales}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="mes" name="Mes" tickFormatter={(v) => `Mes ${v}`} />
                          <YAxis />
                          <RechartsTooltip formatter={(value) => `$${value.toLocaleString('es-CO')}`} />
                          <Legend />
                          <Bar dataKey="comision" name="Comisión ($)" fill="#1890ff" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="Proyecciones Próximos Meses">
                    <Table 
                      columns={proyeccionColumns}
                      dataSource={proyecciones}
                      rowKey="mes"
                      pagination={false}
                      size="small"
                    />
                  </Card>
                  <Card title="Tendencia de Proyección vs Ventas Anteriores" style={{ marginTop: '16px' }}>
                    <div style={{ height: 300, width: '100%' }}>
                      <ResponsiveContainer>
                        <LineChart
                          data={[...comisionesMensuales.map(m => ({ mes: m.mes, comision: m.comision })), ...proyecciones.map(p => ({ mes: p.mes, proyeccion: p.comisionEstimada }))]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="mes" tickFormatter={(v) => `Mes ${v}`} />
                          <YAxis />
                          <RechartsTooltip formatter={(value) => `$${value.toLocaleString('es-CO')}`} />
                          <Legend />
                          <Line type="monotone" dataKey="comision" name="Comisión Real" stroke="#1890ff" activeDot={{ r: 8 }} />
                          <Line type="monotone" dataKey="proyeccion" name="Proyección" stroke="#52c41a" strokeDasharray="5 5" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Simulador Interactivo 🎮" key="simulador">
              <InteractiveSimulator currentUser={user} isAdmin={false} />
            </TabPane>

            <TabPane tab="Comisiones Mensuales" key="monthly">
              <Table 
                columns={monthlyColumns}
                dataSource={comisionesMensuales}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane tab="Comisiones Trimestrales" key="quarterly">
              <Table 
                columns={quarterlyColumns}
                dataSource={comisionesTrimestrales}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane tab="Explicaciones Detalladas" key="explanations">
              <List
                dataSource={comisionesMensuales}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`Mes ${item.mes} - ${item.Medio.nombre}`}
                      description={
                        <div>
                          <p><strong>Comisión:</strong> ${item.comision.toLocaleString('es-CO')}</p>
                          <p><strong>Cumplimiento:</strong> {(item.cumplimiento * 100).toFixed(2)}%</p>
                          <Button 
                            type="link" 
                            onClick={() => handleViewDetails(item)}
                          >
                            Ver Explicación Detallada
                          </Button>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </TabPane>
          </Tabs>
        </Card>
      </Content>

      <Modal
        title="Explicación Detallada del Cálculo"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        style={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        {selectedCalculation && (
          <div>
            <Alert
              message={`Cálculo ${selectedCalculation.tipo === 'mensual' ? 'Mensual' : 'Trimestral'}`}
              description={`Período: ${selectedCalculation.tipo === 'mensual' ? `Mes ${selectedCalculation.mes}` : `Trimestre ${selectedCalculation.trimestre}`}/${selectedCalculation.anio}`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            
            <Timeline>
              <Timeline.Item>
                <Title level={4}>1. Datos Básicos</Title>
                <p><strong>Presupuesto:</strong> ${selectedCalculation.presupuesto.toLocaleString('es-CO')}</p>
                <p><strong>Venta Real:</strong> ${selectedCalculation.ventaReal.toLocaleString('es-CO')}</p>
                <p><strong>Cumplimiento:</strong> {(selectedCalculation.cumplimiento * 100).toFixed(2)}%</p>
              </Timeline.Item>
              
              <Timeline.Item>
                <Title level={4}>2. Banda de Cumplimiento</Title>
                <p><strong>Banda:</strong> {selectedCalculation.banda}</p>
                <p><strong>Factor Individual:</strong> {selectedCalculation.factorIndividual}</p>
                <p><strong>Factor Integral:</strong> {selectedCalculation.factorIntegral}</p>
              </Timeline.Item>
              
              <Timeline.Item>
                <Title level={4}>3. Cálculo de Tasa</Title>
                <p><strong>Tasa Base:</strong> {selectedCalculation.tasaBase}</p>
                <p><strong>Peso Estratégico:</strong> {selectedCalculation.pesoEstrategico || '1.0'}</p>
                <p><strong>Tasa Final:</strong> {selectedCalculation.tasaFinal}</p>
              </Timeline.Item>
              
              <Timeline.Item>
                <Title level={4}>4. Comisión Final</Title>
                <p><strong>Comisión:</strong> ${selectedCalculation.comision.toLocaleString('es-CO')}</p>
                <p><strong>Cumplimiento Integral:</strong> {(selectedCalculation.cumplimientoIntegral * 100).toFixed(2)}%</p>
                <p><strong>¿Puede pagar?:</strong> {selectedCalculation.puedePagar ? 'Sí' : 'No'}</p>
              </Timeline.Item>
              
              {selectedCalculation.explicacion && (
                <Timeline.Item>
                  <Title level={4}>5. Explicación Detallada</Title>
                  <pre style={{ 
                    background: '#f5f5f5', 
                    padding: '16px', 
                    borderRadius: '4px',
                    whiteSpace: 'pre-wrap',
                    fontSize: '12px'
                  }}>
                    {selectedCalculation.explicacion}
                  </pre>
                </Timeline.Item>
              )}
            </Timeline>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default EmployeeDashboard;